import { FileText, Sheet, Image, File, Box, PenTool, FileArchive } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type FileType =
  | "pdf"
  | "word"
  | "excel"
  | "image"
  | "ifc"
  | "model"
  | "drawing"
  | "archive"
  | "file";

interface FileTypeConfig {
  icon: LucideIcon;
  color: string;
  label: string;
}

const FILE_TYPE_CONFIG: Record<FileType, FileTypeConfig> = {
  pdf: {
    icon: FileText,
    color: "text-red-600",
    label: "PDF",
  },
  word: {
    icon: FileText,
    color: "text-blue-600",
    label: "DOCX",
  },
  excel: {
    icon: Sheet,
    color: "text-green-600",
    label: "XLSX",
  },
  image: {
    icon: Image,
    color: "text-purple-600",
    label: "IMG",
  },
  ifc: {
    // BIM model (.ifc) — 3D cube glyph, violet to match the "BIM" source badge
    // shown in the Building Data sidebar (WHA-3642).
    icon: Box,
    color: "text-violet-600",
    label: "IFC",
  },
  model: {
    // Interchange mesh (.fbx) — teal, its own type in the document list rather
    // than sharing the IFC violet.
    icon: Box,
    color: "text-teal-600",
    label: "FBX",
  },
  drawing: {
    // CAD drawing (.dwg/.dxf/.nc1) — orange, matching the document list's own
    // icon map in components/ui/documents/file-type-icon.tsx.
    icon: PenTool,
    color: "text-orange-600",
    label: "DWG",
  },
  archive: {
    icon: FileArchive,
    color: "text-amber-700",
    label: "ZIP",
  },
  file: {
    icon: File,
    color: "text-neutral-500",
    label: "FILE",
  },
};

/**
 * Extension → file type. Kept in step with the document list's own map in
 * components/ui/documents/file-type-icon.tsx: a file must not show an orange
 * DWG icon in one panel and a generic grey glyph in the other.
 */
const EXTENSION_TYPES = new Map<string, FileType>(
  Object.entries({
    pdf: "pdf",
    doc: "word",
    docx: "word",
    xls: "excel",
    xlsx: "excel",
    xlsm: "excel",
    csv: "excel",
    png: "image",
    jpg: "image",
    jpeg: "image",
    gif: "image",
    webp: "image",
    svg: "image",
    bmp: "image",
    tif: "image",
    tiff: "image",
    ifc: "ifc",
    fbx: "model",
    dwg: "drawing",
    dxf: "drawing",
    nc1: "drawing",
    zip: "archive",
    asice: "archive",
    bdoc: "archive",
    ddoc: "archive",
    rar: "archive",
    "7z": "archive",
    tar: "archive",
    gz: "archive",
  } as const)
);

/**
 * Determine the file type from a filename extension.
 */
export function getFileType(filename: string): FileType {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TYPES.get(ext) ?? "file";
}

/**
 * Get the icon configuration for a file type.
 */
export function getFileTypeConfig(fileType: FileType): FileTypeConfig {
  return FILE_TYPE_CONFIG[fileType];
}

/**
 * Get file type icon and color from filename.
 */
export function getFileTypeIcon(filename: string): FileTypeConfig {
  const fileType = getFileType(filename);
  return getFileTypeConfig(fileType);
}

interface FileTypeIconProps {
  filename: string;
  className?: string;
  size?: number;
}

/**
 * Render a file type icon component based on filename extension.
 */
export function FileTypeIcon({ filename, className = "", size = 14 }: FileTypeIconProps) {
  const { icon: Icon, color } = getFileTypeIcon(filename);

  return <Icon className={`${color} ${className}`} size={size} />;
}

/**
 * The structured half of a status event.
 *
 * Both fields are optional because the chatbot does NOT send them yet:
 * `streaming.py` serialises a StatusEvent as `{type, message}` and
 * `EventQueue.emit_status_key` resolves the catalog key to text and drops it.
 * Until a companion backend change carries them, every pill lands in the prose
 * fallback below.
 */
export interface StatusMeta {
  key?: string | null;
  params?: Record<string, unknown> | null;
}

/**
 * A URL is not a filename, however much its tail looks like one.
 *
 * `reading_url` puts the raw WebFetch URL in the message ("Reading
 * https://example.com/spec.pdf..."), and reading the last dot segment of a
 * path as an extension drew a red PDF icon on a web-page fetch — likewise
 * .zip/.gz. No filename contains a slash, so one is enough to reject a token;
 * a status that spells out a folder path loses its icon rather than showing
 * the wrong one. `reading_url`'s catalog params carry `url`, not `filename`,
 * so this stays needed once the backend sends `key` + `params`.
 */
function isUrlLike(token: string): boolean {
  return token.includes("/");
}

/**
 * The first whitespace-delimited token carrying a known extension, or null.
 *
 * A word without a dot is not a filename, and a dot alone is not enough —
 * "v2.1" would qualify — so the extension has to be one the icon map draws.
 */
function findFilenameToken(message: string): string | null {
  for (const token of message.split(/\s+/)) {
    const trimmed = token.replace(/[.,;:!?)\]]+$/, "");
    if (isUrlLike(trimmed)) continue;
    if (trimmed.includes(".") && getFileType(trimmed) !== "file") return trimmed;
  }
  return null;
}

/**
 * The filename a status refers to, or null.
 *
 * Prefers `params.filename` from the backend's catalog entry — once it sends
 * one. Everything below is the fallback, and today it is the only live path.
 *
 * The anchored pattern keeps a name with spaces whole ("Reading Q1 Report.xlsx"),
 * but it only ever recognised two of the thirteen filename-bearing statuses
 * ("Reading x" / "Loen faili x") — every IFC, version-check and comparison
 * status rendered icon-less, and in Estonian all but one did. So a miss falls
 * through to scanning for a token with a known extension anywhere in the
 * message, which covers the other eleven in both languages without this file
 * having to restate the backend's wording. Only the extension is read from the
 * result (it picks the glyph; the pill shows the full message), so losing the
 * words before a space costs nothing.
 */
export function parseStatusFilename(message: string, meta?: StatusMeta): string | null {
  const fromParams = meta?.params?.filename;
  if (typeof fromParams === "string" && fromParams.trim()) return fromParams.trim();

  const anchored = message.match(/^(?:Reading|Loen faili)\s+(.+\.\w+)$/);
  if (anchored && !isUrlLike(anchored[1])) return anchored[1];

  return findFilenameToken(message);
}

/**
 * Check if a status message is a web search status.
 *
 * `searching_web` is the catalog key; the string comparison is the pre-`key`
 * fallback and is exact, so a word changed in the catalog would silently drop
 * the globe.
 */
export function parseStatusWebSearch(message: string, meta?: StatusMeta): boolean {
  if (meta?.key) return meta.key === "searching_web";
  return message === "Searching the web..." || message === "Otsin veebist...";
}
